import { useEffect, useState } from 'react'
import {
  TSalesRep,
  TAccordionElements,
  TInstruments,
  TSpecialRequestModalProps,
  TEquipment,
} from './SpecialRequestModal.types'
import { CustomChangeEvent, Option } from 'common/types'
import { useIsMobile } from 'lib/utils/mediaQueries'
import {
  useCreateSpecialRequestMutation,
  useGetLazyRepsQuery,
  useGetSurgeriesQuery,
} from 'lib/apollo/hooks'
import { enqueueSnackbar } from 'notistack'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import { useEnterpriseStore } from 'store'

export const useSpecialRequestModalLogic = (
  props: TSpecialRequestModalProps
) => {
  const { servicesClient: client } = useApolloClients()
  const { surgeryId, onClose, beforeDate, afterDate } = props
  const isMobile = useIsMobile()

  const [selectedVendor, setSelectedVendor] = useState<Option | null>(null)
  const [instruments, setInstruments] = useState<TInstruments>(null)
  const [selectedCompanies, setSelectedCompanies] = useState<Option[]>([])
  const [linkedSalesReps, setLinkedSalesReps] = useState<TSalesRep[]>([])

  const [equipment, setEquipment] = useState<TEquipment>(null)

  const [isAccordionsElementOpen, setIsAccordionsElementOpen] =
    useState<TAccordionElements>({
      instruments: false,
      implantsBiologics: false,
      salesReps: false,
    })

  const [createSpecialRequest, { loading: loadingCreateSpecialRequest }] =
    useCreateSpecialRequestMutation()

  const [getSalesReps, { data: salesRepsData, loading: loadingSalesReps }] =
    useGetLazyRepsQuery({
      bidCompanyId: Number(selectedVendor?.id),
    })

  const { selectedDepartment, isDepartmentsLoaded } = useEnterpriseStore()

  const { refetch: getSurgeries } = useGetSurgeriesQuery(
    {
      before: beforeDate,
      after: afterDate,
      limit: 0,
      departmentId: selectedDepartment?.id,
    },
    {
      disablePolling: true,
      skip: !isDepartmentsLoaded,
    }
  )

  const salesReps: TSalesRep[] =
    salesRepsData?.getReps?.data
      ?.filter(
        (item) => !linkedSalesReps.find((rep) => rep.auth0Id === item.id)
      )
      .map(({ id, ...rep }) => ({
        ...rep,
        auth0Id: id,
        name: `${rep.lastName}, ${rep.firstName} | ${rep.email}`,
        phoneNumber: rep.mobile,
        notifyEmail: false,
        notifySms: false,
      })) || []

  const salesRepsOptions = salesReps.map(({ name, auth0Id }) => ({
    name,
    id: auth0Id,
  }))

  const groupedRepsByCompany = linkedSalesReps.reduce((acc, rep) => {
    const companyName = rep.companyName
    if (!acc[companyName]) {
      acc[companyName] = []
    }
    acc[companyName].push(rep)
    return acc
  }, {} as Record<string, TSalesRep[]>)

  const disableCreateSpecialRequest =
    loadingCreateSpecialRequest ||
    (!instruments && linkedSalesReps.length === 0)

  const handleAccordionChange = (id: string, expanded: boolean) => {
    setIsAccordionsElementOpen({
      instruments: false,
      implantsBiologics: false,
      salesReps: false,
      [id]: expanded,
    })
  }

  const handleInstrumentsChange = (
    key: 'instruments' | 'notes',
    selectedOptions: string[]
  ) => {
    setInstruments((prev) => {
      const updatedInstruments = {
        ...(prev ?? { instruments: null, notes: null }),
        [key]: selectedOptions.length > 0 ? selectedOptions : null,
      }

      return updatedInstruments?.instruments === null &&
        (updatedInstruments.notes === null ||
          updatedInstruments.notes.filter((item) => item !== '').length === 0)
        ? null
        : {
            notes:
              updatedInstruments?.notes?.filter((item) => item !== '') || null,
            instruments: updatedInstruments?.instruments,
          }
    })
  }

  const handleAddCompany = (option: Option) => {
    if (selectedCompanies?.find((item) => item.id === option.id)) return
    setSelectedCompanies((prev) => {
      return [...prev, option]
    })
  }

  const handleDeleteCompany = (option: Option) => {
    setSelectedCompanies((prev) => {
      return prev.filter((item) => item.id !== option.id)
    })
  }

  const handleAddSalesRep = (event: CustomChangeEvent) => {
    const salesRepOption = event.target.value as Option
    if (linkedSalesReps?.find((item) => item.auth0Id === salesRepOption.id))
      return
    setLinkedSalesReps((prev) => {
      const foundRep = salesReps.find(
        (item) => item.auth0Id === salesRepOption.id
      )
      return foundRep ? [...prev, foundRep] : prev
    })
  }

  const handleDeleteSalesRep = (rep: TSalesRep) => {
    setLinkedSalesReps((prev) => {
      return prev.filter((item) => item.auth0Id !== rep.auth0Id)
    })
  }

  const handleNotifySurgeryToRep = (
    auth0Id: string,
    checked: boolean,
    type: 'notifyEmail' | 'notifySms'
  ) => {
    setLinkedSalesReps((prev) => {
      return prev.map((item) => {
        if (item.auth0Id === auth0Id) {
          return { ...item, [type]: checked }
        }
        return item
      })
    })
  }

  const handleCreateSpecialRequest = () => {
    if (!instruments && !linkedSalesReps) return

    createSpecialRequest({
      client,
      variables: {
        input: {
          surgeryId,
          baseUrl: window.location.origin,
          instrumentsNotes: instruments?.notes || null,
          instruments: instruments?.instruments || null,
          salesReps: linkedSalesReps.map((item) => ({
            auth0Id: item.auth0Id,
            firstName: item.firstName,
            lastName: item.lastName,
            email: item.email,
            phoneNumber: item.phoneNumber,
            companyName: item.companyName,
            bidCompanyId: item.bidCompanyId,
            notifySms: item.notifySms,
            notifyEmail: item.notifyEmail,
          })),
        },
      },
    })
      .then(({ data }) => {
        if (data?.createSpecialRequest?.success) {
          setInstruments(null)
          setLinkedSalesReps([])
          getSurgeries()
          enqueueSnackbar('Special request created successfully', {
            variant: 'success',
          })
          onClose()
        } else {
          enqueueSnackbar('Failed to create special request', {
            variant: 'error',
          })
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to create special request', {
          variant: 'error',
        })
      })
  }

  const handleEquipmentChange = (
    index: number,
    field: string,
    value: string | boolean
  ) => {
    setEquipment((prev) => {
      if (!prev) return { options: [], notes: [] }
      const updatedItems = [...(prev.options || [])]
      if (!updatedItems[index]) {
        updatedItems[index] = {}
      }
      updatedItems[index][field] = value
      return { ...prev, options: updatedItems }
    })
  }

  useEffect(() => {
    if (selectedVendor?.id) {
      getSalesReps()
    }
  }, [selectedVendor?.id, getSalesReps])

  return {
    isMobile,
    surgeryId,
    equipment,
    salesReps,
    instruments,
    selectedVendor,
    loadingSalesReps,
    salesRepsOptions,
    selectedCompanies,
    groupedRepsByCompany,
    isAccordionsElementOpen,
    disableCreateSpecialRequest,
    loadingCreateSpecialRequest,
    setEquipment,
    setInstruments,
    handleAddCompany,
    handleAddSalesRep,
    setSelectedVendor,
    handleDeleteCompany,
    handleDeleteSalesRep,
    handleAccordionChange,
    handleEquipmentChange,
    handleInstrumentsChange,
    handleNotifySurgeryToRep,
    handleCreateSpecialRequest,
  }
}
